import { useState } from 'react';
import { useFormikContext, getIn } from 'formik';
import { ImageUploadButton } from '@low6-pickem/ui';
import type { StyleFieldDef, StyleSchema } from '@low6-pickem/style-config';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { uploadImage } from '@/api/upload';
import { ImageGalleryPickerButton } from './ImageGalleryPicker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface SchemaDrivenStylingFormProps {
  schema: StyleSchema;
  fieldName: string;
  contestId?: string;
  disabled?: boolean;
  tenantCode?: string;
}

export function SchemaDrivenStylingForm({
  schema,
  fieldName,
  contestId,
  disabled,
  tenantCode,
}: SchemaDrivenStylingFormProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {schema.sections.map(section => {
          const isCollapsed = collapsed[section.key] ?? false;
          return (
            <section key={section.title} className="border rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(section.key)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
              >
                <h3 className="text-sm font-semibold">{section.title}</h3>
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
              {!isCollapsed && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                  {section.fields.map(field => (
                    <StyleField
                      key={field.key}
                      field={field}
                      fieldName={fieldName}
                      sectionKey={section.key}
                      contestId={contestId}
                      disabled={disabled}
                      tenantCode={tenantCode}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

interface StyleFieldProps {
  field: StyleFieldDef;
  fieldName: string;
  sectionKey: string;
  contestId?: string;
  disabled?: boolean;
  tenantCode?: string;
}

function StyleField({
  field,
  fieldName,
  sectionKey,
  contestId,
  disabled,
  tenantCode,
}: StyleFieldProps) {
  const formik = useFormikContext<Record<string, unknown>>();
  const sectionPath = `${fieldName}.${sectionKey}`;
  const path = `${sectionPath}.${field.key}`;
  const rawValue = getIn(formik.values, path);
  const value = rawValue ?? '';
  const inputId = `${fieldName}-${sectionKey}-${field.key}`;

  if (field.dependsOn) {
    const depValue = getIn(formik.values, `${sectionPath}.${field.dependsOn.field}`);
    if (depValue !== field.dependsOn.value) {
      return null;
    }
  }

  const setValue = (next: string | number | boolean | undefined) => {
    const parent = (getIn(formik.values, sectionPath) as Record<string, unknown> | undefined) ?? {};
    const isEmpty = next === undefined || next === '' || next === null;
    if (isEmpty) {
      if (Object.prototype.hasOwnProperty.call(parent, field.key)) {
        const { [field.key]: _removed, ...rest } = parent;
        void _removed;
        formik.setFieldValue(sectionPath, rest);
      }
    } else {
      formik.setFieldValue(path, next);
    }
  };

  const labelNode = (
    <Label htmlFor={inputId} className="text-sm font-medium">
      {field.label}
    </Label>
  );

  const wrapWithTooltip = (control: React.ReactNode) =>
    field.description ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{control}</div>
        </TooltipTrigger>
        <TooltipContent>{field.description}</TooltipContent>
      </Tooltip>
    ) : (
      control
    );

  let control: React.ReactNode;

  switch (field.type) {
    case 'color': {
      const stringValue = typeof value === 'string' ? value : '';
      control = (
        <div className="flex gap-2">
          <Input
            id={inputId}
            type="color"
            value={stringValue || '#000000'}
            onChange={e => setValue(e.target.value)}
            disabled={disabled}
            className="w-20 h-10 p-1"
          />
          <Input
            value={stringValue}
            placeholder="#RRGGBB"
            onChange={e => setValue(e.target.value)}
            disabled={disabled}
            className="flex-1"
          />
        </div>
      );
      break;
    }
    case 'image': {
      const stringValue = typeof value === 'string' ? value : '';
      const uploadPath = field.uploadPath?.replace('{contestId}', contestId ?? 'new');
      control = (
        <div className="flex gap-2 items-center">
          <Input
            id={inputId}
            value={stringValue}
            placeholder="https://..."
            onChange={e => setValue(e.target.value)}
            disabled={disabled}
            className="flex-1"
          />
          {uploadPath && (
            <ImageUploadButton
              onUpload={url => setValue(url)}
              path={uploadPath}
              filename={field.key}
              disabled={disabled}
              uploadFn={async data => {
                const result = await uploadImage(data);
                return { imageUrl: result.imageUrl };
              }}
              title={`Upload ${field.label}`}
            />
          )}
          {tenantCode && (
            <ImageGalleryPickerButton
              tenantCode={tenantCode}
              onSelect={url => setValue(url)}
              disabled={disabled}
            />
          )}
        </div>
      );
      break;
    }
    case 'url':
    case 'text': {
      const stringValue = typeof value === 'string' ? value : '';
      control = (
        <Input
          id={inputId}
          value={stringValue}
          placeholder={field.type === 'url' ? 'https://...' : undefined}
          onChange={e => setValue(e.target.value)}
          disabled={disabled}
        />
      );
      break;
    }
    case 'textarea': {
      const stringValue = typeof value === 'string' ? value : '';
      control = (
        <Textarea
          id={inputId}
          value={stringValue}
          rows={4}
          onChange={e => setValue(e.target.value)}
          disabled={disabled}
        />
      );
      break;
    }
    case 'number': {
      const numberValue = typeof value === 'number' ? String(value) : ((value as string) ?? '');
      control = (
        <Input
          id={inputId}
          type="number"
          value={numberValue}
          onChange={e => {
            const raw = e.target.value;
            if (raw === '') {
              setValue(undefined);
            } else {
              const parsed = Number(raw);
              setValue(Number.isNaN(parsed) ? undefined : parsed);
            }
          }}
          disabled={disabled}
        />
      );
      break;
    }
    case 'boolean': {
      const boolValue = value === true;
      control = (
        <Switch
          id={inputId}
          checked={boolValue}
          onCheckedChange={checked => setValue(checked)}
          disabled={disabled}
        />
      );
      break;
    }
    case 'select': {
      const stringValue = typeof value === 'string' ? value : '';
      control = (
        <Select value={stringValue} onValueChange={next => setValue(next)} disabled={disabled}>
          <SelectTrigger id={inputId}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    }
    default:
      control = null;
  }

  return (
    <div className="flex flex-col gap-2">
      {labelNode}
      {wrapWithTooltip(control)}
    </div>
  );
}
